import { useState } from "react";
import { useParams } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import { FormModelInfoWithParameters, CustomModelFormValues } from "../types";
import { mapModelInfoToAPI } from "../utils";
import { useCustomParametersStore } from "../CustomModelsCreation/CustomParameters/custom-parameters-store";

import { getInferenceClientRequestData } from "./utils";

import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { useEditCustomModel } from "@/queries/models/useEditCustomModel";
import { useCreateCustomModel } from "@/queries/models/useCreateCustomModel";
import { useAuth } from "@/context/auth-context";
import { useInferenceClients } from "@/queries";

export const useCustomModelsForm = ({
  setIsOpen,
  methods,
  setActiveStep,
}: {
  setIsOpen: (isOpen: boolean) => void;
  methods: UseFormReturn<CustomModelFormValues>;
  setActiveStep: (activeStep: number) => void;
}) => {
  const store = useCustomParametersStore();
  const { workspaceId } = useParams();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const authContext = useAuth();
  const { mutate: createCustomModel, isLoading: isCreatingPending } = useCreateCustomModel(
    authContext?.userToken ?? "",
  );
  const { mutate: editCustomModel, isLoading: isEditingPending } = useEditCustomModel(
    authContext?.userToken ?? "",
  );
  const queryClient = useQueryClient();
  const { mutate: createInferenceClient, isLoading: isCreatingInferenceClientPending } =
    useInferenceClients(authContext?.userToken ?? "");

  const reset = () => {
    setIsSubmitted(false);
    // Clear params before closing so onOpenChange's areParametersDifferent
    // guard does not block the dialog and leave us stuck on step 1.
    store.reset();
    if (store.isEditing) {
      store.setIsEditing(false);
    }
    methods.reset();
    setActiveStep(0);
    setIsOpen(false);
    queryClient.invalidateQueries({
      queryKey: ["models"],
    });
    queryClient.invalidateQueries(["custom-models"]);
  };

  const onSubmit = () => {
    const data = methods.getValues("create_custom_model");
    const modelId = store.modelsList.find(
      model => model.display_name === data.displayName && model.llm_model_name === data.modelName,
    )?.id;

    const combinedData = {
      ...data,
      id: data.id ?? modelId,
      provider_id: store.providerId,
      parameters: store.parametersList.map(parameter => parameter.getParameter),
    } as FormModelInfoWithParameters;

    const mappedModelInfo = mapModelInfoToAPI(combinedData, Number(workspaceId));

    if (store.isEditing) {
      editCustomModel(mappedModelInfo, {
        onSuccess: reset,
      });
      return;
    }

    createCustomModel(mappedModelInfo, {
      onSuccess: () => {
        if (data.provider === HUGGINGFACE_PROVIDER) {
          const inferenceClientRequestData = getInferenceClientRequestData(
            combinedData,
            store.headers,
            store.cookies,
            store.timeout,
            workspaceId ?? "",
          );

          createInferenceClient(inferenceClientRequestData, {
            onSuccess: reset,
          });

          return;
        }

        reset();
      },
    });
  };

  return {
    isSubmitted,
    setIsSubmitted,
    onSubmit,
    reset,
    isLoading: isCreatingPending || isEditingPending || isCreatingInferenceClientPending,
  };
};
